import { useState } from 'react';
import { api, type Adapter } from '../lib/api.ts';
import { ensureDeviceGranted, forgetGrantedDevice, grantedDeviceName } from '../lib/transports/webbluetooth.ts';
import { describeTransportSupport, type TransportKind } from '../lib/transports/types.ts';

const DEFAULT_NAMES: Record<TransportKind, string> = {
  ble: 'Bluetooth reader',
  serial: 'USB reader',
  wifi: 'Wi-Fi reader',
  sim: 'Simulator',
};

interface Props {
  /** Editing an existing adapter rather than adding one. */
  initial?: Adapter;
  /** Setup has already asked how they connect, so the form does not ask again. */
  fixedTransport?: TransportKind;
  submitLabel?: string;
  onSaved: (adapter: Adapter) => void;
  onCancel?: () => void;
}

/**
 * Add or edit one adapter.
 *
 * Shared by first-run setup and adapter settings, so the two cannot drift into
 * asking for different things.
 */
export function AdapterForm({ initial, fixedTransport, submitLabel, onSaved, onCancel }: Props) {
  const [form, setForm] = useState({
    name: initial?.name ?? '',
    transport: (initial?.transport ?? fixedTransport ?? 'ble') as TransportKind,
    host: initial?.host ?? '192.168.0.10',
    port: initial?.port ?? 35000,
    ssid: initial?.ssid ?? '',
    // The key is never sent to the browser, so this field starts empty and an
    // empty value on save means "keep the stored one".
    wifiKey: '',
  });
  const [paired, setPaired] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [working, setWorking] = useState(false);

  const support = describeTransportSupport(form.transport);

  /**
   * Open Chrome's reader chooser now, from this click.
   *
   * Pairing used to wait for the first scan, so setup ended with nothing to
   * show that the reader worked. Doing it here also names the adapter after the
   * device, which beats "Bluetooth reader" when there are two of them.
   */
  async function pairNow() {
    setError(null);
    try {
      forgetGrantedDevice();
      await ensureDeviceGranted();
      const name = grantedDeviceName();
      setPaired(name ?? 'reader');
      if (name && !form.name) setForm((f) => ({ ...f, name }));
    } catch (err) {
      // A cancelled chooser is a decision, not a failure worth a banner.
      if ((err as Error).name !== 'NotFoundError') setError((err as Error).message);
    }
  }

  async function save() {
    setWorking(true);
    setError(null);
    try {
      const isWifi = form.transport === 'wifi';
      const saved = await api.saveAdapter({
        id: initial?.id,
        name: form.name.trim() || DEFAULT_NAMES[form.transport],
        transport: form.transport,
        host: isWifi ? form.host || null : null,
        port: isWifi ? form.port || null : null,
        ssid: isWifi ? form.ssid || null : null,
        wifiKey: isWifi ? form.wifiKey || undefined : undefined,
      });
      onSaved(saved);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setWorking(false);
    }
  }

  return (
    <div className="stack">
      {!fixedTransport && (
        <label className="field">
          <span>Connects by</span>
          <select
            value={form.transport}
            onChange={(e) => setForm({ ...form, transport: e.target.value as TransportKind })}
            disabled={Boolean(initial)}
          >
            <option value="ble">Bluetooth LE</option>
            <option value="serial">USB / Bluetooth serial</option>
            <option value="wifi">Wi-Fi (via bridge)</option>
            <option value="sim">Simulator</option>
          </select>
        </label>
      )}

      {!support.supported && <div className="banner warn small">{support.reason}</div>}

      {form.transport === 'ble' && !initial && support.supported && (
        <div className="row wrap">
          <button onClick={pairNow} disabled={working}>
            {paired ? 'Pair a different reader' : 'Pair now'}
          </button>
          {paired ? (
            <span className="pill ok">
              <i className="dot" />
              {paired}
            </span>
          ) : (
            <span className="small dim">Plug the reader in and switch the ignition on.</span>
          )}
        </div>
      )}

      {form.transport === 'serial' && !initial && (
        <span className="small dim">
          Chrome asks which port to use on the first scan. A paired Bluetooth Classic reader such as an OBDLink MX shows
          up in that list too.
        </span>
      )}

      <label className="field">
        <span>Name</span>
        <input
          type="text"
          value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
          placeholder={form.transport === 'wifi' ? 'OBDLink MX Wi-Fi' : DEFAULT_NAMES[form.transport]}
        />
      </label>

      {form.transport === 'wifi' && (
        <>
          <label className="field">
            <span>Network name (SSID)</span>
            <input
              type="text"
              value={form.ssid}
              onChange={(e) => setForm({ ...form, ssid: e.target.value })}
              placeholder="printed on the adapter"
            />
          </label>
          <label className="field">
            <span>Network key</span>
            <input
              type="password"
              value={form.wifiKey}
              onChange={(e) => setForm({ ...form, wifiKey: e.target.value })}
              placeholder={initial?.hasWifiKey ? 'leave blank to keep stored key' : 'printed on the adapter'}
              autoComplete="off"
            />
          </label>
          <div className="row">
            <label className="field" style={{ flex: 2 }}>
              <span>Address</span>
              <input type="text" value={form.host} onChange={(e) => setForm({ ...form, host: e.target.value })} />
            </label>
            <label className="field" style={{ flex: 1 }}>
              <span>Port</span>
              <input
                type="number"
                value={form.port}
                onChange={(e) => setForm({ ...form, port: Number(e.target.value) })}
              />
            </label>
          </div>
          <span className="small dim">
            The key is stored on this machine in the bridge database and never sent back to the browser.
          </span>
        </>
      )}

      {error && <div className="banner error small">{error}</div>}

      <div className="row">
        <button className="primary" onClick={save} disabled={working}>
          {submitLabel ?? (initial ? 'Save changes' : 'Add adapter')}
        </button>
        {onCancel && (
          <button className="ghost" onClick={onCancel} disabled={working}>
            Cancel
          </button>
        )}
      </div>
    </div>
  );
}
